/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@apollo/client';
import { Box, Grid, GridItem } from '@chakra-ui/react';

import { OutstandingAgreementType } from '__generated__/GQL';
import asBentoContainer from 'common/bento/hoc/asBentoContainer';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';

import { GetEpAgreementWallContextDocument } from './graphql/queries/getEpAgreementWallContext.gql';
import machineConfig, { model, State } from './machine';
import Hub from './pages/Hub/Hub';
import Introduction from './pages/Introduction/Introduction';
import PersonalStatus from './pages/PersonalStatus/PersonalStatus';
import RegistrationDate from './pages/RegistrationDate/RegistrationDate';
import Success from './pages/Success/Success';

const EpAgreementWall = asBentoContainer(
  () => {
    const history = useHistory();

    const { companyProfileId } = useCompanyContext();

    const exitFlow = useCallback(() => history.push('/'), [history]);

    if (!companyProfileId) {
      exitFlow();

      return null;
    }

    const query = useQuery(GetEpAgreementWallContextDocument, {
      variables: {
        companyProfileId,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(moduleData) => (
          <EpAgreementWallContainer
            key={companyProfileId}
            moduleData={moduleData}
            onDone={exitFlow}
          />
        )}
      </SuspenseQuery>
    );
  },

  {
    layouts: {
      base: ({ children, header }) => (
        <Grid
          height="100vh"
          templateAreas="'header' 'content'"
          templateColumns="1fr minmax(240px,320px);"
          templateRows="fit-content(82px) auto"
        >
          <GridItem gridArea="header">{header}</GridItem>

          <GridItem gridArea="content" paddingBottom="space-24">
            <Box margin="auto" width="400px">
              {children}
            </Box>
          </GridItem>
        </Grid>
      ),
    },
  },
);

const EpAgreementWallContainer = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.INTRODUCTION:
        return (
          <Introduction
            moduleData={moduleData}
            onNext={() => send(model.events.NEXT())}
            onPrev={() => null}
          />
        );

      case State.HUB:
        return (
          <Hub
            moduleData={moduleData}
            onAgreement={() => send(model.events.AGREEMENT())}
            onCompliance={() => send(model.events.COMPLIANCE())}
            onPrev={() => null}
            onRegistrationDate={() => send(model.events.REGISTRATION_DATE())}
          />
        );

      case State.AGREEMENT:
        return (
          // Needed to mask the sidebar as this flow is mounted withing the AppLayout,
          // and this page is supposed to be full screen.
          // TODO(Carina): consider doing this directly in the Agreement page level
          <Box backgroundColor="white" left="0" position="fixed">
            <Agreement
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : send(model.events.PREV())
              }
              type={OutstandingAgreementType.Cgu}
            />
          </Box>
        );

      case State.COMPLIANCE:
        return (
          <PersonalStatus
            moduleData={moduleData}
            onNext={() => send(model.events.NEXT())}
            onPrev={() => send(model.events.PREV())}
          />
        );

      case State.REGISTRATION_DATE:
        return (
          <RegistrationDate
            moduleData={moduleData}
            onNext={() => send(model.events.NEXT())}
            onPrev={() => send(model.events.PREV())}
          />
        );

      case State.SUCCESS:
      case State.DONE:
        return (
          <Success
            moduleData={moduleData}
            onNext={() => send(model.events.NEXT())}
            onPrev={() => send(model.events.PREV())}
          />
        );

      default:
        return assertNever(stringifiedState);
    }
  },

  {
    machineConfig,
  },
);

export default EpAgreementWall;
